// Stay pillar photos (sections/pillar-orbit.liquid, layout "Editorial list"). The active pillar's photo shows in
// the card's stage. A change fades softly (CSS, opacity only; at once with reduced motion): the new photo fades
// in on top while the old ones stay fully visible under it (is-leaving) until it's in, so the card never shows
// between them; the row's text and circle and, below 990px, the caption fade too. The section scrolls like any
// other (client feedback 2026-10-14: no scroll steps).
// - The first pillar is active on load (rendered by the section, so nothing changes when this runs).
// - Autoplay: the next pillar every --pillar-autoplay (CSS: 1.2s from 990px, 2s below, where the caption is
//   read; after the fifth, the first) while the card is in view. It starts once the visitor has arrived (90% of
//   the card on screen, or 90% of the screen filled by a card taller than it) and goes on until less than a third
//   is left. Until then it stands on the first pillar, so a visitor scrolling down arrives at the first one and the
//   first turn comes a full interval later. While it's paused (the card out of view, keyboard focus in it, the
//   tab hidden) the turn keeps its remaining time.
// - A mouse hovering a row (990px+; a moving mouse, not a row scrolled under a resting one) picks it at once
//   and holds the autoplay while it's on the list; leaving the list starts a fresh interval, then the next pillar.
// - A click, tap or keyboard pick stops the autoplay until the card leaves the screen; when it's back (in
//   view) the autoplay goes on from the pillar picked.
// - No autoplay with reduced motion. The selection never resets by itself.
// - Below 990px the caption under the icon row shows the active pillar's name and descriptor (aria-hidden).
// - Below 990px a sideways swipe across the photo (touch or pen) picks the next pillar (to the left) or the
//   previous one (to the right), exactly as a tap on its icon would; from the last it goes on to the first and
//   back, as the autoplay does. It counts only past 40px and mostly sideways (more than 1.5 times the vertical
//   distance), so a tap or a vertical scroll never turns it; the stage leaves vertical scrolling and zoom to the
//   browser (touch-action, CSS).
// The buttons carry the meaning (their text is read out even where it's hidden); aria-pressed tells which
// pillar's photo shows and the hidden photos are aria-hidden. Autoplay changes aren't announced (no live region).
if (!customElements.get('stay-pillar-photos')) {
  customElements.define(
    'stay-pillar-photos',
    class StayPillarPhotos extends HTMLElement {
      connectedCallback() {
        this.card = this.querySelector('.pillar-orbit__editorial');
        this.list = this.querySelector('.pillar-orbit__rows');
        this.caption = this.querySelector('.pillar-orbit__caption');
        this.photos = [...this.querySelectorAll('[data-pillar-photo]')];
        this.rows = [...this.querySelectorAll('button[data-pillar]')];
        if (!this.card || !this.list || !this.rows.length) return;

        this.wide = window.matchMedia('(min-width: 990px)');
        this.still = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.current = this.rows.find((row) => row.getAttribute('aria-pressed') === 'true')?.dataset.pillar;
        // 'auto' (turning, or paused), 'held' (a mouse on the list) or 'stopped' (picked by hand)
        this.mode = 'auto';
        this.inView = false;
        this.focused = false;
        this.remaining = null;
        this.layer = 1;
        this.photos.find((photo) => photo.classList.contains('is-active'))?.style.setProperty('z-index', '1');

        // A pick by hand: shows the pillar and stops the autoplay until the card leaves the screen
        const pick = (row) => {
          this.mode = 'stopped';
          this.show(row.dataset.pillar);
          this.update();
        };
        this.rows.forEach((row) => {
          row.addEventListener('click', () => pick(row));
          // Keyboard focus (Tab) picks; a click's focus is handled by the click
          row.addEventListener('focus', () => {
            if (row.matches(':focus-visible')) pick(row);
          });
          // Hover: only a mouse that moves (when the page scrolls a row under a resting mouse, the browser's
          // synthetic events have no movement)
          row.addEventListener('pointermove', (event) => {
            if (event.pointerType !== 'mouse' || !this.wide.matches) return;
            if (!event.movementX && !event.movementY) return;
            if (this.mode === 'auto') this.mode = 'held';
            this.show(row.dataset.pillar);
            this.update();
          });
        });
        // Swipe on the photo (below 990px): a touch pointer is captured by the stage, so its pointerup comes here
        // even off the photo; a vertical scroll cancels it (pointercancel)
        const stage = this.querySelector('.pillar-orbit__stage');
        stage?.addEventListener('pointerdown', (event) => {
          this.swipe = null;
          if (event.pointerType === 'mouse' || !event.isPrimary || this.wide.matches) return;
          this.swipe = { id: event.pointerId, x: event.clientX, y: event.clientY };
        });
        stage?.addEventListener('pointerup', (event) => {
          const swipe = this.swipe;
          this.swipe = null;
          if (!swipe || swipe.id !== event.pointerId) return;
          const dx = event.clientX - swipe.x;
          const dy = event.clientY - swipe.y;
          if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy) * 1.5) return;
          const index = this.rows.findIndex((row) => row.dataset.pillar === this.current);
          const step = dx < 0 ? 1 : -1;
          pick(this.rows[(index + step + this.rows.length) % this.rows.length]);
        });
        stage?.addEventListener('pointercancel', () => {
          this.swipe = null;
        });

        this.list.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'mouse' || this.mode !== 'held') return;
          this.mode = 'auto';
          this.remaining = null;
          this.update();
        });

        // Keyboard focus in the card pauses; the focus a click or tap leaves on a row doesn't (the pick already
        // stopped the autoplay, and it should go on once the card has left the screen and come back)
        this.card.addEventListener('focusin', (event) => {
          this.focused = event.target.matches(':focus-visible');
          this.update();
        });
        this.card.addEventListener('focusout', (event) => {
          if (this.card.contains(event.relatedTarget)) return;
          this.focused = false;
          this.update();
        });

        this.onChange = () => this.update();
        document.addEventListener('visibilitychange', this.onChange);
        this.still.addEventListener('change', this.onChange);
        this.wide.addEventListener('change', this.onChange);

        // In view (2026-10-15; half of the card before, which started the turns while the visitor was still
        // above, so they arrived mid-round): from 90% of the card, or of the screen for a card taller than the
        // screen, down to a third
        const start = 0.9;
        const keep = 1 / 3;
        this.observer = new IntersectionObserver(
          ([entry]) => {
            const screen = entry.rootBounds?.height || window.innerHeight;
            const shown = entry.isIntersecting
              ? Math.max(entry.intersectionRatio, entry.intersectionRect.height / screen)
              : 0;
            this.inView = shown >= (this.inView ? keep : start);
            // Out of the screen: a pick by hand is let go, the autoplay goes on from it next time
            if (!entry.isIntersecting && this.mode !== 'auto') {
              this.mode = 'auto';
              this.remaining = null;
            }
            this.update();
          },
          { threshold: Array.from({ length: 21 }, (_, i) => i / 20) }
        );
        this.observer.observe(this.card);

        // Theme editor: selecting a pillar block shows it and stops the autoplay
        this.onBlockSelect = (event) => {
          const row = this.rows.find((button) => button.closest('[data-shopify-editor-block]') === event.target);
          if (row) pick(row);
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      disconnectedCallback() {
        clearTimeout(this.timer);
        this.observer?.disconnect();
        document.removeEventListener('visibilitychange', this.onChange);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        this.still?.removeEventListener('change', this.onChange);
        this.wide?.removeEventListener('change', this.onChange);
      }

      // The interval, from the CSS
      interval() {
        return this.duration('--pillar-autoplay', 1200) || 1200;
      }

      // Runs or pauses the turn's timer, which keeps its remaining time through a pause
      update() {
        const auto = this.mode === 'auto' && !this.still.matches;
        const running = auto && this.inView && !this.focused && !document.hidden;

        if (this.started != null) {
          this.remaining -= performance.now() - this.started;
          this.started = null;
        }
        clearTimeout(this.timer);
        if (!auto) {
          this.remaining = null;
          return;
        }
        if (this.remaining == null) this.remaining = this.interval();
        if (!running) return;
        this.started = performance.now();
        this.timer = setTimeout(() => {
          this.started = null;
          this.remaining = null;
          this.next();
          this.update();
        }, Math.max(0, this.remaining));
      }

      next() {
        const index = this.rows.findIndex((row) => row.dataset.pillar === this.current);
        this.show(this.rows[(index + 1) % this.rows.length].dataset.pillar);
      }

      show(id) {
        if (!id || id === this.current) return;
        this.current = id;
        this.layerPhotos(id);
        this.rows.forEach((row) => row.setAttribute('aria-pressed', String(row.dataset.pillar === id)));
        this.classList.add('has-active');
        this.updateCaption(id);
      }

      // The new photo goes on top of the stack and fades in; the one it covers (and any still under it after a fast
      // run of hovers) stays opaque until the new one is in, then they all go (unseen, under it)
      layerPhotos(id) {
        const incoming = this.photos.find((photo) => photo.dataset.pillarPhoto === id);
        if (!incoming) return;
        this.photos.forEach((photo) => {
          if (photo === incoming) return;
          if (photo.classList.contains('is-active')) photo.classList.add('is-leaving');
          photo.classList.remove('is-active');
          photo.setAttribute('aria-hidden', 'true');
        });
        this.layer += 1;
        incoming.style.zIndex = String(this.layer);
        incoming.classList.remove('is-leaving');
        incoming.classList.add('is-active');
        incoming.removeAttribute('aria-hidden');

        clearTimeout(this.layerTimer);
        const settle = () => {
          clearTimeout(this.layerTimer);
          incoming.removeEventListener('transitionend', onEnd);
          if (!incoming.classList.contains('is-active')) return;
          this.photos.forEach((photo) => {
            photo.classList.remove('is-leaving');
            if (photo !== incoming) photo.style.zIndex = '';
          });
          incoming.style.zIndex = '1';
          this.layer = 1;
        };
        const onEnd = (event) => {
          if (event.target === incoming && event.propertyName === 'opacity') settle();
        };
        const fade = this.still.matches ? 0 : this.duration('--pillar-photo-fade', 500);
        if (!fade) {
          settle();
          return;
        }
        incoming.addEventListener('transitionend', onEnd);
        // In case the transition doesn't run (an image not yet decoded, a hidden tab)
        this.layerTimer = setTimeout(settle, fade + 100);
      }

      // A duration custom property from the CSS, in ms
      duration(name, fallback) {
        const value = getComputedStyle(this).getPropertyValue(name).trim();
        const ms = value.endsWith('ms') ? parseFloat(value) : parseFloat(value) * 1000;
        return Number.isFinite(ms) ? ms : fallback;
      }

      // The caption under the icon row (below 990px): the old text fades out (--pillar-caption-out), the new one is
      // written while nothing shows and fades in rising 4px (--pillar-caption-in). Changes that come fast (quick
      // taps, a turn mid-fade) only update the text waiting to be written, so the last pick is the one that shows
      // and nothing blinks. At once with reduced motion and from 990px (the caption is hidden there).
      updateCaption(id) {
        if (!this.caption) return;
        const row = this.rows.find((button) => button.dataset.pillar === id);
        this.captionNext = {
          name: row?.querySelector('.pillar-orbit__name')?.textContent.trim() || '',
          descriptor: row?.querySelector('.pillar-orbit__descriptor')?.textContent.trim() || '',
        };
        const write = () => {
          this.caption.querySelector('.pillar-orbit__caption-name').textContent = this.captionNext.name;
          this.caption.querySelector('.pillar-orbit__caption-descriptor').textContent = this.captionNext.descriptor;
        };

        if (this.still.matches || this.wide.matches) {
          clearTimeout(this.captionTimer);
          cancelAnimationFrame(this.captionFrame);
          this.captionStage = null;
          this.caption.classList.remove('is-leaving', 'is-entering');
          write();
          return;
        }
        // Fading out, or written and about to fade in: the new text is the one that will show
        if (this.captionStage === 'leaving') return;
        if (this.captionStage === 'entering') {
          write();
          return;
        }

        this.captionStage = 'leaving';
        this.caption.classList.add('is-leaving');
        this.captionTimer = setTimeout(() => {
          write();
          this.captionStage = 'entering';
          this.caption.classList.add('is-entering');
          this.caption.classList.remove('is-leaving');
          // One frame at the start position, then the fade in
          this.captionFrame = requestAnimationFrame(() => {
            this.captionFrame = requestAnimationFrame(() => {
              this.captionStage = null;
              this.caption.classList.remove('is-entering');
            });
          });
        }, this.duration('--pillar-caption-out', 150));
      }
    }
  );
}
